import forge from 'apis/forge';
import ActionButton from 'components/Widget/ActionButton';
import { IconType } from 'components/Icon';
import { EXPORT_CODE_FRAMEWORKS, MAX_LLM_EXPORT_FRAMES } from 'dragonmouse-shared/api/export';
import { FrameStatus } from 'dragonmouse-shared/interfaces';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';

const CUSTOM_FALLBACK_INSTRUCTIONS =
	'Produce a complete runnable project for the framework named above. Include all config, ' +
	'build, and entry files the framework needs. Use the framework’s idiomatic file layout, ' +
	'routing, and styling conventions.';

interface Props {
	label: string;
	framework: string;
	frameworkInstructions?: string;
	frameIds: string[];
}

const ExportCodeAction = ({ label, framework, frameworkInstructions, frameIds }: Props) => {
	const [isExporting, setIsExporting] = useState(false);
	const { activeScene, allFrames } = useSelector(
		(r: RootReducer) => ({
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
			allFrames: selectFrames(r.db),
		}),
		shallowEqual
	);

	const framesFound = frameIds
		.map((id) => allFrames.find((f) => f.id === id))
		.filter((f): f is NonNullable<typeof f> => !!f);
	const allReady =
		framesFound.length === frameIds.length &&
		framesFound.every((f) => f.metadata.status === FrameStatus.READY);

	const onClick = async () => {
		if (isExporting) return;
		if (frameIds.length === 0) {
			toast.error('No frames selected to export.');
			return;
		}
		if (framesFound.length < frameIds.length) {
			toast.error('Some frames are no longer available.');
			return;
		}
		if (!allReady) return;
		if (frameIds.length > MAX_LLM_EXPORT_FRAMES) {
			toast.error(`Code export is limited to ${MAX_LLM_EXPORT_FRAMES} frames.`);
			return;
		}

		const filename = buildExportFilename(activeScene?.title, framework, 'project');
		const isKnown = (EXPORT_CODE_FRAMEWORKS as readonly string[]).includes(framework);
		setIsExporting(true);
		try {
			await runExportWithToast({
				pendingLabel: `Generating your ${framework} project — this may take a few minutes…`,
				run: async () => {
					const { exportId } = await forge.exportFramesCode({
						frameIds,
						framework,
						customInstructions: isKnown
							? undefined
							: frameworkInstructions?.trim() || CUSTOM_FALLBACK_INSTRUCTIONS,
						filename,
					});
					return { exportId, filename };
				},
			});
		} catch (e) {
			Sentry.captureException(e);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<ActionButton
			icon={IconType.DOWNLOAD}
			label={label}
			loadingLabel="Exporting frames…"
			isLoading={isExporting}
			disabledReason={!allReady ? 'Waiting for frames to finish generating.' : undefined}
			onClick={onClick}
		/>
	);
};

export default ExportCodeAction;
